import { get as _get, set } from "lodash"
import { put, call, takeEvery } from "redux-saga/effects"
import { createActions, createReducer } from "reduxsauce"
import update from "immutability-helper"
import { get, post } from "api"
import { Types as UpdaterTypes } from "./updater"
import { Types as CreatorTypes } from "./creator"
import { Types as GraphqlTypes } from "./graphql"
import { Types as DeleterTypes } from "./deleter"
import { OK, LOADING, ERROR } from "helpers/data-statuses"

export const { Types, Creators } = createActions({
  load: ["resource", "id", "url", "payload", "noToken"],
  loadSuccess: ["resource", "id", "data"],
  updateSuccess: ["resource", "id", "payload", "result", "options"],
  intradayLoadSuccess: ["data"],
  loadError: ["resource", "id", "error"],
  resetCollections: ["resources"],
  graphqlCreateSuccess: ["resource", "id", "nodes", "data"],
}, {
  prefix: "ompnt/modules/loader/",
})

const DEFAULT_STATE = {}

function setState(state, resource, data) {
  return { ...state, [resource]: data }
}

function setStates(state, resources, data) {
  const newStates = {}

  resources.forEach(resource => {
    newStates[resource] = data
  })

  return { ...state, ...newStates }
}

function updateData(data, result) {
  return data.map(datum => {
    if ((datum.id && (datum.id === result.id)) || (datum._id && (datum._id === result._id))) {
      return result
    } else {
      return datum
    }
  })
}

function deleteItem(data, id) {
  return data.filter(datum => {
    if ((datum.id && (datum.id === id)) || (datum._id && (datum._id === id))) {
      return false
    } else {
      return true
    }
  })
}

function handleLoad(state, { resource, id }) {
  if (!id) {
    return setState(state, resource, { status: OK, id: null, data: null, apiMessages: { errors: null, messages: null }, error: null })
  } else {
    return setState(state, resource, { status: LOADING, id, data: null, apiMessages: { errors: null, messages: null }, error: null })
  }
}

function handleLoadError(state, { resource, id, error }) {
  return setState(state, resource, { status: ERROR, id, data: null, apiMessages: { errors: null, messages: null }, error })
}

function handleLoadSuccess(state, { resource, id, data, apiMessages }) {
  return setState(state, resource, { status: OK, id, data, apiMessages, error: null })
}

function handleResetCollections(state, { resources }) {
  return setStates(state, resources, {})
}

function handleUpdateSuccess(state, { resource, id, payload, result, options }) {
  const status = _get(state, [resource, "status"])
  const currentId = _get(state, [resource, "id"])
  if (status === OK && currentId === id) {
    return setState(state, resource, {
      status: OK,
      id,
      data: { ...state[resource].data, ...payload },
      error: null,
    })
  } else if (status === OK && options && options.updateCollection === currentId) {
    return setState(state, resource, {
      status: OK,
      id: currentId,
      data: updateData(state[resource].data, result),
      error: null,
    })
  } else {
    return state
  }
}

function handleDeleteSuccess(state, { resource, deletedId, options }) {
  const status = _get(state, [resource, "status"])
  const currentId = _get(state, [resource, "id"])
  if (status === OK && options && options.updateCollection === currentId) {
    return setState(state, resource, {
      status: OK,
      id: currentId,
      data: deleteItem(state[resource].data, deletedId),
      error: null,
    })
  } else {
    return state
  }
}

function handleCreateSuccess(state, { resource, id, result }) {
  const status = _get(state, [resource, "status"])
  const currentId = _get(state, [resource, "id"])
  if (status === OK && currentId === id) {
    return setState(state, resource, {
      status: OK,
      id,
      data: [...state[resource].data, result],
      error: null,
    })
  } else {
    return state
  }
}

function handleGraphQLCreateSuccess(state, { resource, id, nodes, data }) {
  if (state[resource]) {
    const status = _get(state, [resource, "status"])
    const currentId = _get(state, [resource, "id"])
    const originalData = _get(state[resource].data, nodes)
    if (originalData) {
      const newData = set({}, nodes, [...originalData, data])

      if (status === OK && currentId === id) {
        return setState(state, resource, {
          status: OK,
          id,
          data: newData,
          error: null,
        })
      }
    }
  }

  return state
}

const handleIntradayLoadSuccess = (state, { data }) => update(state, { $merge: data })

export const reducer = createReducer(DEFAULT_STATE, {
  [Types.LOAD]: handleLoad,
  [Types.LOAD_SUCCESS]: handleLoadSuccess,
  [Types.LOAD_ERROR]: handleLoadError,
  [Types.RESET_COLLECTIONS]: handleResetCollections,
  [Types.GRAPHQL_CREATE_SUCCESS]: handleGraphQLCreateSuccess,
  [Types.INTRADAY_LOAD_SUCCESS]: handleIntradayLoadSuccess,
  [UpdaterTypes.UPDATE_SUCCESS]: handleUpdateSuccess,
  [Types.UPDATE_SUCCESS]: handleUpdateSuccess,
  [CreatorTypes.CREATE_SUCCESS]: handleCreateSuccess,
  [DeleterTypes.DELETE_SUCCESS]: handleDeleteSuccess,
  [GraphqlTypes.LOAD]: handleLoad,
  [GraphqlTypes.LOAD_SUCCESS]: handleLoadSuccess,
  [GraphqlTypes.LOAD_ERROR]: handleLoadError,
})

const currentActions = {}

function* loadSaga({ resource, id, url, payload, noToken }) {
  if (url) {
    // Check if we have an active job loading the same collection and id
    const uniqueId = `${resource}_${url}`
    const existingJob = currentActions[uniqueId]

    // Only start the loader if we are not currently loading the same collection and id
    if (!existingJob) {
      currentActions[uniqueId] = true
      try {
        let data
        if (!payload) {
          data = yield call(get, url, noToken)
        } else {
          data = yield call(post, url, payload, noToken)
        }

        yield put(Creators.loadSuccess(resource, id, data))
      } catch (error) {
        yield put(Creators.loadError(resource, id, error))
      } finally {
        // clears the key in the jobs lookup
        delete currentActions[uniqueId]
      }
    }
  }
}

export function* saga() {
  yield takeEvery(Types.LOAD, loadSaga)
}
